/**
 * Shared plumbing for the MUI Autocompletes that replace react-widgets.
 *
 * react-widgets took `textField`/`valueField`/`dataKey` as key names or accessors and was happy
 * with primitives, objects or a mix. MUI wants functions returning strings and a value that is one
 * of its own options. These adapters sit between the two so each renderer stays readable, and so a
 * renderer as small as PrePing's does not have to import the 900-line redux-form renderer module
 * to reach them.
 */
import React from 'react';
import ListSubheader from '@mui/material/ListSubheader';

/**
 * react-widgets' `textField` is a key or an accessor; MUI wants a function returning a string,
 * and throws in the console for anything else — accessors here can return undefined.
 *
 * The primitive fallback matters: several call sites pass a plain string array *and* a
 * `textField`, which react-widgets resolved to the item itself. Looking the key up on a string
 * yields undefined, so without this the list would render blank rows.
 */
export const toOptionLabel = (textField) => (option) => {
  if (option === null || option === undefined) return '';
  let raw;
  if (typeof textField === 'function') {
    raw = textField(option);
  } else if (textField && typeof option === 'object') {
    raw = option[textField];
  } else {
    raw = option;
  }
  if (raw === null || raw === undefined) {
    return typeof option === 'object' ? '' : String(option);
  }
  return String(raw);
};

/**
 * Two options are the same when the field the caller names matches. MUI needs that twice: to match
 * the value to an option, and as each listed option's React key — which defaults to the label, and
 * duplicate labels (several offer actions share a name) leave stale rows behind as typing narrows
 * the list. Without `valueField` there is nothing to go on but MUI's defaults.
 *
 * Returns the props to spread onto an Autocomplete.
 */
export const toOptionIdentity = (valueField) => {
  if (!valueField) return {};
  const key = (candidate) => (candidate && typeof candidate === 'object' ? candidate[valueField] : candidate);
  return {
    isOptionEqualToValue: (option, value) => key(option) === key(value),
    getOptionKey: key,
  };
};

/**
 * The stored value may be the option object, or just the value field — redux-form keeps whichever
 * the caller's `onChange` wrote, and a `defaultValue` is usually the raw value. Autocomplete needs
 * the option itself, and warns about a value that matches none of them, so map back here. `''` is
 * redux-form's empty; MUI's is null.
 */
export const toOptionValue = (options, valueField, raw) => {
  if (raw === '' || raw === null || raw === undefined) return null;
  if (typeof raw !== 'object' && valueField) {
    return (options || []).find((item) => item?.[valueField] === raw) ?? null;
  }
  return raw;
};

/**
 * The same for a multi-select's array. An entry that matches no option is kept as it is rather
 * than dropped: with `allowCreate` the freshly typed string is exactly that.
 */
export const toOptionValues = (options, valueField, list) => {
  if (!Array.isArray(list)) return [];
  if (!valueField) return list;
  return list.map((entry) => toOptionValue(options, valueField, entry) ?? entry);
};

/**
 * react-widgets' grouping, in MUI's terms. Its `groupBy` could return anything — every caller here
 * returns 0 or 1 — and `renderListGroup` turned that into a heading. MUI groups by a *string*, and
 * renders a fresh heading each time the group changes as it walks the list, so it also needs the
 * options pre-sorted or one group yields several headings.
 *
 * Returns the props to spread onto an Autocomplete; `{}` when the caller does not group.
 */
export function useGroupedOptions(options, groupBy, renderListGroup) {
  const list = options ?? [];
  if (!groupBy) return { options: list };

  const read = (option) => (typeof groupBy === 'function' ? groupBy(option) : option?.[groupBy]);
  const sorted = [...list].sort((a, b) => String(read(a)).localeCompare(String(read(b))));
  // The heading callback wants whatever the caller's groupBy returned, not MUI's string key.
  const raw = new Map();

  return {
    options: sorted,
    groupBy: (option) => {
      const value = read(option);
      const key = String(value);
      raw.set(key, value);
      return key;
    },
    renderGroup: renderListGroup
      ? (params) => (
          <li key={params.key}>
            <ListSubheader component='div' sx={{ lineHeight: 2, bgcolor: 'background.paper' }}>
              {renderListGroup({ group: raw.get(params.group) })}
            </ListSubheader>
            <ul style={{ padding: 0 }}>{params.children}</ul>
          </li>
        )
      : undefined,
  };
}
